import React, { useEffect, useRef, useState } from "react";

interface OverlayedImageProps {
  baseImageUrl: string;
  overlayImageUrl: string;
  size?: number;
  className?: string;
  alt?: string;
}

function getSquareCrop(
  imgWidth: number,
  imgHeight: number
): { sx: number; sy: number; sw: number; sh: number } {
  if (imgWidth === imgHeight) {
    return { sx: 0, sy: 0, sw: imgWidth, sh: imgHeight };
  }
  if (imgWidth > imgHeight) {
    // Wider than tall: crop sides
    const offset = (imgWidth - imgHeight) / 2;
    return { sx: offset, sy: 0, sw: imgHeight, sh: imgHeight };
  } else {
    // Taller than wide: crop top/bottom
    const offset = (imgHeight - imgWidth) / 2;
    return { sx: 0, sy: offset, sw: imgWidth, sh: imgWidth };
  }
}

const OverlayedImage: React.FC<OverlayedImageProps> = ({
  baseImageUrl,
  overlayImageUrl,
  size = 400,
  className = "",
  alt = "",
}) => {
  const [combinedUrl, setCombinedUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!baseImageUrl || !overlayImageUrl) {
      setCombinedUrl(null);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const baseImg = new window.Image();
    const overlayImg = new window.Image();
    baseImg.crossOrigin = "anonymous";
    overlayImg.crossOrigin = "anonymous";

    let loaded = 0;

    function tryDraw() {
      loaded++;
      if (loaded < 2) return;
      const canvasNow = canvasRef.current;
      if (!canvasNow) return;
      const ctx = canvasNow.getContext("2d");
      if (!ctx) return;

      // --- Central square crop ---
      const { sx, sy, sw, sh } = getSquareCrop(baseImg.width, baseImg.height);

      ctx.clearRect(0, 0, size, size);
      // Draw cropped user image to fill canvas
      ctx.drawImage(baseImg, sx, sy, sw, sh, 0, 0, size, size);
      // Draw overlay (assumed 1:1)
      ctx.drawImage(overlayImg, 0, 0, size, size);

      setCombinedUrl(canvasNow.toDataURL("image/png"));
    }

    baseImg.onload = tryDraw;
    overlayImg.onload = tryDraw;
    baseImg.src = baseImageUrl;
    overlayImg.src = overlayImageUrl;

    return () => setCombinedUrl(null);

  }, [baseImageUrl, overlayImageUrl, size]);

  return (
    <>
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        style={{ display: "none" }}
        aria-hidden="true"
      />
      {combinedUrl && (
        <img
          src={combinedUrl}
          alt={alt}
          className={className}
          draggable={false}
          width={size}
          height={size}
        />
      )}
    </>
  );
};

export default OverlayedImage;
